import { addDays, endOfDay, isSameDay, isWithinInterval, startOfDay } from 'date-fns';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { GanttConfig } from '../Gantt/GanttConfig';
import { createSelector } from 'reselect';
import { useGanttStore } from '../Gantt/store';

export type DateRange = { from: Date; to: Date };

export const GanttFilter = {
  TO_ACCEPT: 'toAccept',
  SHOW_WITHOUT_TASKS: 'showWithoutTasks',
  PRIORITY: 'priority',
  DELAYED: 'delayed',
} as const;

export type GanttFilter = (typeof GanttFilter)[keyof typeof GanttFilter];

type ProductionPlanFilterStore = {
  ganttDateRange: DateRange;
  ganttFilters: GanttFilter[];
  setGanttDateRange: (instanceId: string, dateRange: DateRange) => void;
  setGanttFilters: (instanceId: string, ganttFilters: GanttFilter[]) => void;
  reset: () => void;
};

export const useGanttFilterStore = create(
  immer<ProductionPlanFilterStore>((set, get, store) => {
    return {
      ganttDateRange: {
        from: startOfDay(addDays(new Date(), -GanttConfig.LOAD_BEFORE)),
        to: endOfDay(addDays(new Date(), GanttConfig.LOAD_AFTER)),
      },
      ganttFilters: [],
      setGanttDateRange: (instanceId: string, { from, to }: DateRange) => {
        const previousState = get();
        const start = startOfDay(from);
        const end = endOfDay(to);

        set({ ganttDateRange: { from: start, to: end } });

        if (
          previousState.ganttDateRange.from.getTime() !== start.getTime() ||
          previousState.ganttDateRange.to.getTime() !== end.getTime()
        ) {
          useGanttStore.getState().clearGanttInstanceData(instanceId);
        }
      },
      setGanttFilters: (instanceId: string, ganttFilters: GanttFilter[]) => {
        set({ ganttFilters });
        useGanttStore.getState().clearGanttInstanceData(instanceId);
      },
      reset: () => set(store.getInitialState()),
    };
  }),
);

export const isCurrentGanttDateWithinRangeSelector = createSelector(
  (state: ProductionPlanFilterStore) => state.ganttDateRange,
  (dateRange) => isWithinInterval(new Date(), { start: dateRange.from, end: dateRange.to }),
);

export const isInitialGanttState = createSelector(
  (state: ProductionPlanFilterStore) => state.ganttDateRange,
  (state: ProductionPlanFilterStore) => state.ganttFilters,
  (ganttDateRange, ganttFilters) => {
    const initialState = useGanttFilterStore.getInitialState().ganttDateRange;
    return (
      isSameDay(ganttDateRange.from, initialState.from) &&
      isSameDay(ganttDateRange.to, initialState.to) &&
      ganttFilters.length === 0
    );
  },
);
